import { useState, useEffect } from "react";
import { X, Download, Loader2 } from "lucide-react";
import api from "../../lib/api";

import jsPDF from "jspdf";
// @ts-ignore
import { autoTable } from "jspdf-autotable";
import logoImage from "../../assets/logo/logo.png";

export default function InvoiceDetailsModal({ invoiceId, onClose }) {
  const [invoiceDetails, setInvoiceDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!invoiceId) return;

    setLoading(true);
    setError("");
    api
      .get(`/company/invoices/${invoiceId}`)
      .then((res) => {
        if (res.data && res.data.success && res.data.data) {
          // Check if payment type is stripe to determine fuel surcharge source
          let fuelSurcharge = res.data.quote_fuel;
          let creditCardFees = 0;
          if (res.data.type === "stripe") {
            fuelSurcharge = res.data.additional_fees;
            creditCardFees = res.data.percentigFee;
          }

          setInvoiceDetails({
            ...res.data.data,
            api_summary: {
              price: res.data.price,
              report_generation_fees: res.data.report_generation_fees,
              quote_fuel: fuelSurcharge,
              credit_card_fees: creditCardFees,
              total: res.data.total
            }
          });
        } else {
          setError("Failed to load invoice details.");
        }
      })
      .catch(() => setError("Failed to load invoice details."))
      .finally(() => setLoading(false));
  }, [invoiceId]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  const formatMoney = (value) => {
    if (value == null || value === undefined) return "$0.00";
    return `$${parseFloat(value).toFixed(2)}`;
  };

  const formatDate = (value) => {
    if (!value) return "—";
    return new Date(value).toLocaleString();
  };

  const toLabel = (key) => {
    const labels = {
      id: "ID",
      total: "Total",
      admin_fee: "Admin Fee",
      company_net_amount: "Net Amount",
      created_at: "Created At",
      updated_at: "Updated At",
      services: "Services",
      first_name: "First Name",
      last_name: "Last Name",
      email: "Email",
      phone: "Phone",
      company_name: "Company Name",
      street_1: "Street 1",
      street_2: "Street 2",
      postal_code: "Zip Code",
      country: "Country",
      name: "Name",
      owner_name: "Owner Name",
      website_url: "Website",
      location_text: "Location",
      timezone: "Timezone",
      role: "Role",
    };
    return labels[key] || key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const generatePDF = () => {
    if (!invoiceDetails) return;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    let yPos = margin;

    try {
      const logoSize = 30;
      const logoX = pageWidth - margin - logoSize;
      const logoY = margin;
      doc.addImage(logoImage, "PNG", logoX, logoY, logoSize, logoSize);
    } catch (error) {
      console.warn("Could not add logo:", error);
    }

    doc.setFontSize(18);
    doc.text("Invoice Details", margin, yPos);
    yPos += 10;

    doc.setFontSize(14);
    doc.text(`Invoice #${invoiceDetails.id}`, margin, yPos);
    yPos += 15;

    const addSection = (title, data) => {
      if (yPos > 250) {
        doc.addPage();
        yPos = margin;
      }

      doc.setFontSize(12);
      doc.setFont(undefined, "bold");
      doc.text(title, margin, yPos);
      yPos += 8;

      const rows = [];
      Object.entries(data).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== "") {
          let displayValue = value;
          if (Array.isArray(value)) {
            displayValue = value.join(", ");
          } else if (
            (typeof value === "number" && (key.includes("fee") || key.includes("amount") || key === "total"))
          ) {
            displayValue = formatMoney(value);
          } else if (key.includes("_at") || key.includes("date")) {
            displayValue = formatDate(value);
          } else if (typeof value === "object") {
            return;
          }
          rows.push([toLabel(key), String(displayValue)]);
        }
      });

      if (rows.length > 0) {
        autoTable(doc, {
          startY: yPos,
          head: [["Key", "Value"]],
          body: rows,
          theme: "striped",
          headStyles: { fillColor: [20, 128, 128] },
          margin: { left: margin, right: margin },
          styles: { fontSize: 9 },
        });
        yPos = doc.lastAutoTable.finalY + 10;
      }
    };

    const summaryData = {
      id: invoiceDetails.id,
      price: formatMoney(invoiceDetails.api_summary?.price),
      report_generation_fees: formatMoney(invoiceDetails.api_summary?.report_generation_fees),
      fuel_surcharge: formatMoney(invoiceDetails.api_summary?.quote_fuel),
      credit_card_fees: invoiceDetails.api_summary?.credit_card_fees > 0 ? formatMoney(invoiceDetails.api_summary?.credit_card_fees) : undefined,
      total_amount: formatMoney(invoiceDetails.api_summary?.total),
      created_at: invoiceDetails.created_at,
      updated_at: invoiceDetails.updated_at,
      services: invoiceDetails.services,
    };
    addSection("Summary", summaryData);

    if (invoiceDetails.client) {
      addSection("Client Information", invoiceDetails.client);
    }

    if (invoiceDetails.company) {
      const { city_id, state_id, commission_percentage, ...companyDataWithoutIds } = invoiceDetails.company;
      addSection("Company Information", companyDataWithoutIds);
    }



    yPos += 5;
    doc.setFontSize(8);
    doc.setFont(undefined, "normal");
    doc.setTextColor(128, 128, 128);
    doc.text(
      `Generated: ${new Date().toLocaleString()}`,
      pageWidth - margin,
      doc.internal.pageSize.getHeight() - 10,
      { align: "right" }
    );

    doc.save(`invoice-${invoiceDetails.id}.pdf`);
  };

  if (!invoiceId) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-[80]"
        onClick={onClose}
      />
      <div
        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100%-0.25rem)] sm:w-[calc(100%-0.75rem)] md:max-w-4xl lg:max-w-5xl xl:max-w-6xl bg-white rounded-xl sm:rounded-2xl shadow-2xl z-[100] max-h-[90vh] sm:max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-3 sm:p-4 md:p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
            <h2 className="text-base sm:text-lg md:text-xl font-bold text-gray-900 truncate">
              Invoice #{invoiceId}
            </h2>
            {invoiceDetails?.status && (
              <span className="px-2 py-0.5 sm:px-2.5 sm:py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium flex-shrink-0">
                {invoiceDetails.status}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
            {invoiceDetails && (
              <button
                onClick={generatePDF}
                className="px-2 py-1 sm:px-3 sm:py-1.5 border border-gray-300 rounded-lg text-xs font-medium flex items-center gap-1 sm:gap-1.5 hover:bg-gray-50 transition"
              >
                <Download className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Download </span>
                <span className="sm:hidden">PDF</span>
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1.5 sm:p-2 hover:bg-gray-100 rounded-lg transition"
            >
              <X className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-3 sm:p-4 md:p-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-8 sm:py-12">
              <Loader2 className="w-8 h-8 sm:w-12 sm:h-12 text-teal-600 animate-spin mb-3 sm:mb-4" />
              <p className="text-sm sm:text-base text-gray-600">Loading invoice details...</p>
            </div>
          ) : error ? (
            <div className="text-center py-8 sm:py-12">
              <p className="text-sm sm:text-base text-red-600 mb-3 sm:mb-4">{error}</p>
              <button
                onClick={onClose}
                className="px-3 py-1.5 sm:px-4 sm:py-2 bg-gray-100 rounded-lg text-xs sm:text-sm hover:bg-gray-200 transition"
              >
                Close
              </button>
            </div>
          ) : invoiceDetails ? (
            <div className="space-y-4 sm:space-y-5 md:space-y-6">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2 sm:mb-3 text-sm sm:text-base">
                  Summary
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 bg-gray-50 rounded-lg p-3 sm:p-4">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Price</p>
                    <p className="font-medium text-gray-900">
                      {formatMoney(invoiceDetails.api_summary?.price || invoiceDetails.total)}
                    </p>
                  </div>
                  {parseFloat(invoiceDetails.api_summary?.report_generation_fees || 0) > 0 && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Report Generation Fees</p>
                      <p className="font-medium text-gray-900">
                        {formatMoney(invoiceDetails.api_summary.report_generation_fees)}
                      </p>
                    </div>
                  )}
                  {parseFloat(invoiceDetails.api_summary?.quote_fuel || 0) > 0 && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Fuel Surcharge</p>
                      <p className="font-medium text-gray-900">
                        {formatMoney(invoiceDetails.api_summary.quote_fuel)}
                      </p>
                    </div>
                  )}
                  {parseFloat(invoiceDetails.api_summary?.credit_card_fees || 0) > 0 && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Credit Card Fees</p>
                      <p className="font-medium text-gray-900">
                        {formatMoney(invoiceDetails.api_summary.credit_card_fees)}
                      </p>
                    </div>
                  )}
                  <div className="sm:col-span-2 pt-2 border-t border-gray-200 mt-2">
                    <p className="text-sm font-bold text-gray-900 mb-1">Total Amount</p>
                    <p className="text-lg font-bold text-teal-600">
                      {formatMoney(invoiceDetails.api_summary?.total)}
                    </p>
                  </div>


                  {invoiceDetails.services && (
                    <div className="sm:col-span-2">
                      <p className="text-xs text-gray-500 mb-1">Services</p>
                      <div className="flex flex-wrap gap-1.5 sm:gap-2 mt-1">
                        {Array.isArray(invoiceDetails.services) ? (
                          invoiceDetails.services.map((service, idx) => (
                            <span
                              key={idx}
                              className="px-2.5 py-1 bg-teal-100 text-teal-700 rounded-full text-xs font-medium"
                            >
                              {typeof service === 'object' ? service.name : service}
                            </span>
                          ))
                        ) : (
                          <span className="px-2.5 py-1 bg-teal-100 text-teal-700 rounded-full text-xs font-medium">
                            {typeof invoiceDetails.services === 'object' ? invoiceDetails.services.name : invoiceDetails.services}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  {invoiceDetails.created_at && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Created At</p>
                      <p className="font-medium text-gray-900">
                        {formatDate(invoiceDetails.created_at)}
                      </p>
                    </div>
                  )}
                  {invoiceDetails.updated_at && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Updated At</p>
                      <p className="font-medium text-gray-900">
                        {formatDate(invoiceDetails.updated_at)}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {invoiceDetails.client && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2 sm:mb-3 text-sm sm:text-base">
                    Client Information
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 bg-gray-50 rounded-lg p-3 sm:p-4">
                    {invoiceDetails.client.first_name && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">First Name</p>
                        <p className="font-medium text-gray-900">
                          {invoiceDetails.client.first_name}
                        </p>
                      </div>
                    )}
                    {invoiceDetails.client.last_name && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Last Name</p>
                        <p className="font-medium text-gray-900">
                          {invoiceDetails.client.last_name}
                        </p>
                      </div>
                    )}
                    {invoiceDetails.client.email && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Email</p>
                        <p className="font-medium text-gray-900">
                          {invoiceDetails.client.email}
                        </p>
                      </div>
                    )}
                    {invoiceDetails.client.phone && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Phone</p>
                        <p className="font-medium text-gray-900">
                          {invoiceDetails.client.phone}
                        </p>
                      </div>
                    )}
                    {invoiceDetails.client.company_name && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Company Name</p>
                        <p className="font-medium text-gray-900">
                          {invoiceDetails.client.company_name}
                        </p>
                      </div>
                    )}
                    {invoiceDetails.client.street_1 && (
                      <div className="sm:col-span-2">
                        <p className="text-xs text-gray-500 mb-1">Address</p>
                        <p className="font-medium text-gray-900">
                          {invoiceDetails.client.street_1}
                          {invoiceDetails.client.street_2 && `, ${invoiceDetails.client.street_2}`}
                          {invoiceDetails.client.postal_code && `, ${invoiceDetails.client.postal_code}`}
                          {invoiceDetails.client.country && `, ${invoiceDetails.client.country}`}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {invoiceDetails.company && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2 sm:mb-3 text-sm sm:text-base">
                    Company Information
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 bg-gray-50 rounded-lg p-3 sm:p-4">
                    {invoiceDetails.company.name && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Name</p>
                        <p className="font-medium text-gray-900">
                          {invoiceDetails.company.name}
                        </p>
                      </div>
                    )}
                    {invoiceDetails.company.owner_name && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Owner Name</p>
                        <p className="font-medium text-gray-900">
                          {invoiceDetails.company.owner_name}
                        </p>
                      </div>
                    )}
                    {invoiceDetails.company.email && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Email</p>
                        <p className="font-medium text-gray-900">
                          {invoiceDetails.company.email}
                        </p>
                      </div>
                    )}
                    {invoiceDetails.company.phone && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Phone</p>
                        <p className="font-medium text-gray-900">
                          {invoiceDetails.company.phone}
                        </p>
                      </div>
                    )}
                    {invoiceDetails.company.website_url && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Website</p>
                        <p className="font-medium text-gray-900">
                          {invoiceDetails.company.website_url}
                        </p>
                      </div>
                    )}
                    {invoiceDetails.company.location_text && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Location</p>
                        <p className="font-medium text-gray-900">
                          {invoiceDetails.company.location_text}
                        </p>
                      </div>
                    )}
                    {invoiceDetails.company.country && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Country</p>
                        <p className="font-medium text-gray-900">
                          {invoiceDetails.company.country}
                        </p>
                      </div>
                    )}
                    {invoiceDetails.company.timezone && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Timezone</p>
                        <p className="font-medium text-gray-900">
                          {invoiceDetails.company.timezone}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}


            </div>
          ) : null}
        </div>
      </div>
    </>
  );
}

